import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { getCookie, setCookie } from 'core/utils/cookie-helper';
import modelPropTypes, { topicAttributes } from 'core/utils/prop-types/model';

import withTopic from 'core/hocs/withTopic';

import StylesPortal from './StylesPortal';

import {
  COOKIE_NAME,
  STEP_SIZE,
  DIRECTIONS,
  TARGETS,
  MAX_RATE,
  MIN_RATE,
  DEFAULT_RATE,
} from './constants';

import styles from './index.styl';

function getSelector(element) {
  const elementHasClass = Array.prototype.filter.call(element.classList, className => className.indexOf('jsx-') === -1).length > 0;
  /**
   * Если у элемента нет собственных классов, используем классы родителя.
   * Если этого будет недостаточно, придётся рекурсией искать первого предка этого
   * элемента, у которого есть класс, и добавлять nodeName каждого уровня в selectorPostfix.
   */
  const referenceElement = elementHasClass
    ? element
    : element.parentNode;
  const selectorPostfix = elementHasClass
    ? ''
    : ` ${element.nodeName}`;
  const classList = referenceElement.classList;
  return `.${Array.prototype.join.call(classList, '.')}` + selectorPostfix;
}

function getElementFontSize(element) {
  return parseFloat(window.getComputedStyle(element).getPropertyValue('font-size'));
}

/**
 * @author Galianov Artem <a.galianov@rambler-co.ru>
 *
 * Здесь не следует использовать хок withoutSSR, т.к это компонент
 * общего назначения и вложеные withoutSSR могут приводить к ошибкам.
 * Подробнее в readme компонента withoutSSR.
 */
class FontSizeChanger extends Component {
  constructor(props) {
    super(props);

    this.fontSizesMap = {};
    this.rules = '';

    this.state = {
      fontSizeRate: DEFAULT_RATE,
    };
  }

  componentDidMount() {
    this.changeFontSize();
  }

  componentDidUpdate(prevProps) {
    if (this.props.topic?.content?.id !== prevProps.topic?.content?.id) {
      this.changeFontSize(null, true);
    }
  }

  onButtonClick = direction => () => {
    return this.changeFontSize(direction);
  };

  buildRules = (rate, shouldUpdateFontSizesMap) => {
    const isDefaultRate = rate === DEFAULT_RATE;
    const lineHeightFix = isDefaultRate ? '' : 'line-height: 1.2em !important;';
    const commonRules = TARGETS.join() + `{${lineHeightFix}}`;

    if (isDefaultRate) return commonRules;

    const isFontSizesMapEmpty = Object.keys(this.fontSizesMap).length === 0;

    if (shouldUpdateFontSizesMap || isFontSizesMapEmpty) {
      this.buildFontSizesMap();
    }

    const specificRules = Object.keys(this.fontSizesMap)
      .map(selector => `${selector}{font-size: ${this.fontSizesMap[selector] * rate / 100}px !important}`)
      .join('');

    return commonRules + specificRules;
  };

  /**
   * @param {string} direction
   * @param {*} shouldUpdateFontSizesMap - проверять ли страницу на появление новых элементов,
   *                                       совпадающих с TARGETS перед изменением размера шрифта;
   *                                       например, это нужно при переходах "топик > топик".
   */
  changeFontSize = (direction, shouldUpdateFontSizesMap) => {
    let fontSizeRate = +getCookie(COOKIE_NAME) || DEFAULT_RATE;

    if (direction) {
      fontSizeRate = direction === DIRECTIONS.UP
        ? fontSizeRate + STEP_SIZE
        : fontSizeRate - STEP_SIZE;
    }

    this.rules = this.buildRules(fontSizeRate, shouldUpdateFontSizesMap);

    this.setState({ fontSizeRate }, () => {
      setCookie(COOKIE_NAME, fontSizeRate);
    });
  };

  buildFontSizesMap = () => {
    const matchedNodeList = document.querySelectorAll(TARGETS.join(','));
    Array.prototype.forEach.call(matchedNodeList, element => {
      const selector = getSelector(element);
      if (!this.fontSizesMap[selector]) {
        this.fontSizesMap[selector] = getElementFontSize(element);
      }
    });
  };

  render() {
    const { fontSizeRate } = this.state;

    return (
      <div className={styles.wrapperSizeButtons}>
        <button
          disabled={fontSizeRate === MIN_RATE}
          className={cx(styles.button, styles.down)}
          onClick={this.onButtonClick(DIRECTIONS.DOWN)}
        >a</button>
        <button
          disabled={fontSizeRate === MAX_RATE}
          className={cx(styles.button, styles.up)}
          onClick={this.onButtonClick(DIRECTIONS.UP)}
        >A</button>
        <StylesPortal>{this.rules}</StylesPortal>
      </div>
    );
  }
}

FontSizeChanger.propTypes = {
  topic: PropTypes.shape({
    content: modelPropTypes(topicAttributes),
  }),
};

export default withTopic(FontSizeChanger);
